import { KFDataTable } from "@kf/ic2fx";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material";
import { GridColumnHeaderParams } from "@mui/x-data-grid/models";
import { range } from "lodash";
import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Arrow from "../../../assets/icons/table-arrow.svg";
import { JobPricingReportConstants } from "../../../constants/JobPricingReportConstants";
import { getLanguageCode } from "../../../utilities/helper";
import { PAGE_URL } from "../../../constants/urls.constants";
import {
  JobPricingHeaderDetails,
  JobPricingReportViewBy
} from "../../../helpers/models/JobPricingDetailModels";
import {
  getPricingFiltersThunk,
  updateReportHeaderDetails,
  updateReportViewBy
} from "../../../store/slices/jobPricingDetailsSlice";
import { SJPRListTypes } from "../../../store/slices/jobPricingListSlice";
import { updateJobPricingPayloads } from "../../../store/slices/jobPricingSearchSlice";
import {
  JobPricingListThunk,
  resetSearchKey
} from "../../../store/slices/SJPR/listSlice";
import { LoadingOverlay, NoDataListScreen } from "../NoData/ListScreen";
import "./functions-module.scss";

const FunctionsList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useNavigate();
  const { jobPricingList, country } = useSelector((state: any) => {
    return state;
  });
  const [functionList, setFunctionList] = useState([]);
  const [rowData, setRowData] = useState<any>();
  const [gradeSpanList, setGradeSpanList] = useState<any[]>([]);
  const [selectedGrades, setSelectedGrades] = useState<any[]>([]);
  const [isGradeSelectionPopup, setIsGradeSelectionPopup] =
    useState<boolean>(false);

  const gradeSelectionPopupOpen = (status: boolean) => {
    setIsGradeSelectionPopup(status);
    setSelectedGrades([]);
  };

  const getGradeIDRange = (gradeSpan: string) => {
    if (!gradeSpan) return "";
    const gradeRange = gradeSpan.split("-");
    if (!gradeRange || gradeRange.length === 0) return "";
    return range(+gradeRange[0], +gradeRange[1] + 1).join();
  };

  const isGradesSequential = (grades: string[]) => {
    for (let i = 1; i < grades.length; i++) {
      if (Number(grades[i]) !== Number(grades[i - 1]) + 1) {
        return false;
      }
    }
    return true;
  };

  const getDisplayGradeSpan = (gradeList: string[]) => {
    if (gradeList.length > 1 && isGradesSequential(gradeList)) {
      return gradeList[0] + " - " + gradeList[gradeList.length - 1];
    } else {
      return gradeList
        .sort((a: any, b: any) => a.match(/\d+/) - b.match(/\d+/))
        .join(", ");
    }
  };

  const redirectToReportPricingDetails = (item: any) => {
    dispatch(resetSearchKey());
    sessionStorage.removeItem("searchKey");
    dispatch(updateReportViewBy(JobPricingReportViewBy.FUNCTION));
    const updatedPricingPayloads = {
      searchCategory: JobPricingReportViewBy.FUNCTION,
      jobKey: "ALL",
      jobOrgCode: "ALL",
      jobName: item?.functionTitle || "ALL",
      jobFamilyName: "ALL",
      jobSubFamilyName: "ALL",
      marketJobFamily: (item?.jobFamilyKey || "ALL").toString(),
      marketJobSubFamily: "ALL",
      totalJobs: item?.totalJobs || "0",
      jobsInOrg: item?.jobsInOrg || "0",
      gradeId:
        selectedGrades && selectedGrades.length > 0
          ? selectedGrades
              .sort((a: any, b: any) => a.match(/\d+/) - b.match(/\d+/))
              .join()
          : "ALL",
      gradeSpan: item?.gradeSpan || "ALL",
      searchTerm: item?.functionTitle || ""
    };
    const pricingHeaderDetails = {
      title: item?.functionTitle || "",
      description: "",
      gradeSpan:
        selectedGrades && selectedGrades.length > 0
          ? getDisplayGradeSpan(selectedGrades)
          : "-",
      totalJobs: item?.totalJobs || "0",
      orgJobs: item?.jobsInOrg || "0"
    } as JobPricingHeaderDetails;
    dispatch(updateJobPricingPayloads(updatedPricingPayloads));
    dispatch(updateReportHeaderDetails(pricingHeaderDetails));
    history(PAGE_URL.JOB_PRICING_DETAILS);
  };

  const onSelectRowData = (item: any) => {
    const gradeSpans = getGradeIDRange(item?.gradeSpan);
    if (gradeSpans) {
      const gradeList = gradeSpans.split(",");
      if (gradeList.length > 1) {
        setGradeSpanList(gradeList);
        setRowData(item);
        gradeSelectionPopupOpen(true);
      } else {
        selectedGrades.push(...gradeList);
        setSelectedGrades([...selectedGrades]);
        redirectToReportPricingDetails(item);
      }
    } else {
      redirectToReportPricingDetails(item);
    }
  };

  const handleGradeSelection = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      selectedGrades.push(event.target.name);
    } else {
      selectedGrades.splice(selectedGrades.indexOf(event.target.name), 1);
    }
    setSelectedGrades([...selectedGrades]);
  };

  const FunctionListColumns: any = [
    {
      field: "functionTitle",
      headerName: t("pa.functionTitle"),
      flex: 4,
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "totalJobs",
      headerName: t("pa.headerTotalJobs"),
      flex: 2,
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "gradeSpan",
      headerName: t("pa.gradeSpan"),
      flex: 2,
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "jobsInOrg",
      flex: 2,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: (params: GridColumnHeaderParams) => (
        <div className="columnHeaderJobInOrg">
          <strong style={{ marginRight: "3px" }}>{t("pa.headerJobsInOrg")}</strong>
          <Tooltip
            classes={{ popper: "MuiTooltip-popper-columnHeaderJobInOrg" }}
            placement="right"
            title={
              <>
                <Typography variant="caption">
                  Jobs within your organization that you have permission to view
                  pay data
                </Typography>
              </>
            }
            arrow
          >
            <InfoIcon fontSize="small" sx={{ color: "#0062CC" }} />
          </Tooltip>
        </div>
      )
    },
    {
      field: "totalIncumbents",
      headerName: t("pa.totalIncumbents"),
      sortable: false,
      disableColumnMenu: true,
      flex: 2
    },
    {
      field: "",
      headerName: " ",
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: () => {
        return (
          <div>
            <img src={Arrow} alt="Details icon" />
          </div>
        );
      }
    }
  ];

  const getFunctionsList = (searchKey?: string) => {
    setFunctionList([]);
    let updatedFilterPayload: any = { ...jobPricingList?.filters };
    for (const key in updatedFilterPayload) {
      if (updatedFilterPayload[key]?.length === 0) {
        delete updatedFilterPayload[key];
      }
    }
    let data: any;
    if (
      jobPricingList?.searchKey?.length > 0 ||
      (searchKey && searchKey?.length > 0)
    ) {
      data = {
        countryCode: country?.selectedCountry?.key,
        filters: updatedFilterPayload || {},
        language:
          getLanguageCode(),
        search: jobPricingList?.searchKey || searchKey || ""
      };
    } else {
      data = {
        countryCode: country?.selectedCountry?.key,
        filters: updatedFilterPayload || {},
        language:
          getLanguageCode()
      };
    }

    if (jobPricingList?.filters?.customGradeId) {
      data.gradeSetId = jobPricingList.filters.customGradeId;
    }

    if (jobPricingList?.peerGroupId) {
      data.peerGroupId = jobPricingList.peerGroupId;
    }

    const payLoad = {
      category: SJPRListTypes.FUNCTION,
      data
    };
    dispatch(getPricingFiltersThunk(data));
    dispatch(JobPricingListThunk(payLoad));
  };

  useEffect(() => {
    if (jobPricingList?.filters?.customGradeId !== undefined) {
      const stringSearchKey = sessionStorage.getItem("searchKey") || "";
      stringSearchKey
        ? getFunctionsList(JSON.parse(stringSearchKey))
        : getFunctionsList("");
    }
  }, [jobPricingList?.filters?.customGradeId]);

  useEffect(() => {
    if (jobPricingList?.list?.functions) {
      setFunctionList(
        jobPricingList?.list?.functions?.map((item: any) => {
          return {
            ...item,
            id: item?.jobFamilyKey
          };
        })
      );
    } else {
      setFunctionList([]);
    }
  }, [jobPricingList?.list?.functions]);

  return (
    <div
      className={`app-table functions-table-container ${
        jobPricingList?.isListloading || functionList.length === 0
          ? "grid-overlay-height"
          : ""
      }`}
    >
      <KFDataTable
        sx={{
          "& .MuiDataGrid-cell:hover": {
            cursor: "pointer"
          },
          "& .kfic2-button.MuiIconButton-root.kfic2-ghostSecondary:focus, & .kfic2-button.MuiIconButton-root.kfic2-ghostSecondary:hover, & .kfic2-button.MuiIconButton-root.kfic2-ghostSecondary, & .kfic2-button.MuiIconButton-root.kfic2-ghostSecondary:active ":
            {
              color: "var(--button-text)",
              borderColor: "var(--button-text)"
            },
          "& .css-i4bv87-MuiSvgIcon-root": {
            fill: "var(--button-text)"
          }
        }}
        paginationLabel={{
          "Items per page:": t("pa.pagination.itemsPerPage"),
          of: t("pa.pagination.of"),
          items: t("pa.pagination.items"),
          "more than": t("pa.pagination.moreThan"),
          page: t("pa.pagination.page"),
          pages: t("pa.pagination.pages")
        }}
        rowsPerPageOptions={
          JobPricingReportConstants.TABLE_ROWS_PER_PAGE_OPTIONS
        }
        sortingOrder={JobPricingReportConstants.TABLE_SORTING_ORDER}
        initialState={{
          pagination: {
            pageSize: JobPricingReportConstants.TABLE_DEFAULT_PAGE_SIZE
          }
        }}
        rows={functionList || []}
        columns={FunctionListColumns}
        loading={jobPricingList?.isListloading}
        onCellClick={(params: any, event, details) => {
          onSelectRowData(params.row);
        }}
        components={{
          NoRowsOverlay: () => <NoDataListScreen />,
          LoadingOverlay: () => (
            <LoadingOverlay columsList={FunctionListColumns} />
          )
        }}
      />
      <Dialog
        open={isGradeSelectionPopup}
        onClose={(e) => gradeSelectionPopupOpen(false)}
        scroll="paper"
        fullWidth={true}
        maxWidth={"md"}
        className="grade-selection-dialog"
        aria-labelledby="grade-selection-dialog-label"
        aria-describedby="grade-selection-dialog-description"
      >
        <div className="grade-selection-dialog-title">
          <DialogTitle
            id="grade-selection-dialog-title"
            sx={{ fontWeight: "700", padding: 0 }}
          >
            {t("pa.sjpr.selectGradesPricing")}
          </DialogTitle>
          <IconButton
            aria-label="close"
            title="Close"
            disableRipple
            onClick={(e) => gradeSelectionPopupOpen(false)}
            sx={{ padding: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent>
          <Typography
            variant="body1"
            sx={{ paddingBottom: 2 }}
            dangerouslySetInnerHTML={{
              __html: t("pa.sjpr.selectGradesPricingMsg")
            }}
          ></Typography>
          {selectedGrades.length === 3 && (
            <div className="max-grade-selection-msg">
              <Alert
                icon={
                  <CheckCircleIcon fontSize="small" sx={{ color: "#00631C" }} />
                }
                severity="success"
              >
                {t("pa.sjpr.maxSelectGradesMsg")}
              </Alert>
            </div>
          )}
          <div className="grade-selection-container">
            <FormGroup>
              {gradeSpanList.map((grade: string, index: number) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox name={grade} onChange={handleGradeSelection} />
                  }
                  label={grade}
                  disabled={
                    selectedGrades.length === 3 &&
                    !selectedGrades.includes(grade)
                  }
                />
              ))}
            </FormGroup>
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button
            variant="outlined"
            onClick={(e) => gradeSelectionPopupOpen(false)}
          >
            {t("pa.back")}
          </Button>
          <Button
            variant="contained"
            disabled={selectedGrades.length === 0}
            onClick={(e) => redirectToReportPricingDetails(rowData)}
            sx={{
              backgroundColor: "var( --primary-color)",
              color: "var(--contained-button-text)",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: "var(--primary-color)"
              }
            }}
          >
            {t("pa.continue")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FunctionsList;
